<template>
  <div class="gw-step generic-border" :data-id="index">
    <DraggingIndication v-if="isSortable" />

    <!-- first column, name, objective, duration -->
    <div class="gw-step__basics">
      <EditableLabel
        ref="name"
        v-model="stepCopy.name"
        class="gw-step__field"
        :legend="$t('message.STEP_NB', { 'stepNumber': index + 1 })"
        :placeholder="$t('message.STEP_NB', { 'stepNumber': index + 1 })"
        @input="edit('name')"
        @click.native="$refs.name.doFocus()"
      />
      <EditableLabel
        ref="objective"
        v-model="stepCopy.objective"
        class="gw-step__field"
        :legend="$t('message.GW_OBJECTIVE')"
        :placeholder="$t('message.GW_OBJECTIVE_PLACEHOLDER')"
        @input="edit('objective')"
        @click.native="$refs.objective.doFocus()"
      />
      <button
        class="gw-step__basics__delete generic-border generic-border--text-btn generic-border--red"
        @click="onDeleteButton"
      >
        <Icon>{{ deleteMedium }}</Icon>
        <span>{{ $t('message.DELETE') }}</span>
      </button>
    </div>

    <!-- second column, teams, locations -->
    <div class="gw-step__details">
      <AdvancedTeamPicker
        v-model="auditedTeam"
        :legend="`${$t('message.TEAM')}`"
        class="gw-step__field"
        display-all-teams
        ok-button
        single-value
        use-editable-label
      />
      <button
        v-if="stepCopy.auditedTeam != 0"
        class="ml10 add-button blue unselect-team"
        @click.stop="auditedTeam = 0"
      >
        {{ $t('message.UNSELECT_TEAM') }}
      </button>
      <!-- TODO Keep this ? <div class="field-hint">{{ $t('message.GW_STEP_TEAM_HINT') }}</div> -->
      <TreeField
        ref="zonesTree"
        v-model="stepCopy.zone"
        class="gw-step__field"
        :legend="$t('message.LOCATION')"
        :items="allZones"
        :placeholder="$t('message.LOCATION')"
        @input="edit('zone')"
      />
      <div class="gw-step__field gw-step__duration">
        <div class="legend legend--bottom-margin">{{ $t('message.DURATION') }}</div>
        <VueTimepicker
          :key="timePickerKey"
          v-model="duration"
          v-click-outside="rmkTimepicker"
          format="HH:mm:ss"
          :second-interval="5"
        />
      </div>
    </div>

    <!-- right side checklists and delete btn -->
    <div class="gw-step__checklists">
      <EditableLabelRadio
        ref="checklists"
        v-model="checklistIds"
        class="gw-step__field"
        label-key="name"
        :legend="$t('message.GW_CHECKLISTS')"
        :items="allChecklists"
        separator=", "
        multiple
        no-text
        @input="checkListEdit"
      />
      <TransitionGroup name="fade">
        <div
          v-for="(checklist, clIndex) in selectedChecklists"
          :key="checklist.id"
          class="v-centerer gw-step__checklists__selected"
        >
          <Checkbox
            :value="true"
            :title="checklist.name"
            @input="clCheckboxInput(checklist, clIndex)"
          />
          <Icon @click.native="$emit('editChecklist', checklist)">{{ editMedium }}</Icon>
          <Icon v-if="checklist.template === 0" class="delete" @click.native="deleteChecklist(checklist)">
            {{ deleteMedium }}
          </Icon>
        </div>
      </TransitionGroup>
      <button
        class="generic-border generic-border--text-btn generic-border--blue-border gw-step__checklists__add"
        @click="$emit('newCheckList', value)"
      >
        <div class="generic-border__left">
          <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
          <span>{{ $t('message.GW_NEW_CL') }}</span>
        </div>
        <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Component used to setup a step of a GW template.
 * Trigger edition XHRs from the component directly.
 */

import toastr from 'toastr';
import { isEqual } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
// Components
import VueTimepicker from 'vue2-timepicker';
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import TreeField from '@/components/tree-field.vue';
// Utils
import { SET_GW_STEP, DELETE_GW_CHECKLIST } from '@/store/modules/gemba-walks/action-types';
import { calert, decodeHTML, secondsToArray } from '@/utils/main';
import {
  addExtraBold,
  arrowDownLine,
  editMedium,
  deleteMedium,
} from '@/assets/icons/';
import { GW_CHECKLIST_ITEM_TYPE } from '@/utils/gw-utils';

const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');


export default {

  name: 'GwStep',

  components: {
    AdvancedTeamPicker,
    Checkbox,
    DraggingIndication,
    EditableLabel,
    EditableLabelRadio,
    Icon,
    VueTimepicker,
    TreeField,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: -1,
    },
    allZones: {
      type: Array,
      default: () => [],
    },
    allChecklists: {
      type: Array,
      default: () => [],
    },
    isSortable: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      editMedium,
      deleteMedium,
      stepCopy: {},
      timePickerKey: 1,
      allowEdits: false,
    };
  },

  computed: {
    /** Array: Checklists of this step */
    selectedChecklists() {
      // When editing a template, checklists is an array of ids
      // When editing a real GW, checklists is an array of checklist objects copied from checklist templates
      if (this.stepCopy.checklists.length) {
        const [first] = this.stepCopy.checklists;
        if (first instanceof Object) {
          return this.stepCopy.checklists;
        }
      }
      const checklistsPerId = this.allChecklists.reduce((acc, checklist) => {
        acc[`${checklist.id}`] = checklist;
        return acc;
      }, {});
      return this.stepCopy.checklists.map(clId => checklistsPerId[`${clId}`]).filter(cl => cl !== undefined);
    },
    /** Number: ID of the team audited by this step */
    auditedTeam: {
      get() {
        return [this.stepCopy.auditedTeam];
      },
      set(teams) {
        this.stepCopy.auditedTeam = teams && teams.length ? teams[0] : 0;
        this.edit('auditedTeam');
      },
    },
    /** Object: Duration for vue-timepicker */
    duration: {
      get() {
        const [HH, mm, ss] = secondsToArray(this.stepCopy.duration);
        return { HH: HH.length === 1 ? `0${HH}` : HH, mm, ss };
      },
      set(timeValue) {
        this.stepCopy.duration = String(
          Number(timeValue.HH) * 3600
          + Number(timeValue.mm) * 60
          + Number(timeValue.ss),
        );
      },
    },
    /** Array: Array of IDs of checklists for the EditableLabelRadio value */
    checklistIds: {
      get() {
        if (this.stepCopy.checklists.length) {
          const [first] = this.stepCopy.checklists;
          if (first instanceof Object) {
            return this.stepCopy.checklists.map(cl => cl.id);
          }
        }
        return this.stepCopy.checklists;
      },
      set(checklistIds) {
        this.stepCopy.checklists = checklistIds;
        this.edit('checklists');
      },
    },
  },

  watch: {
    value: {
      deep: true,
      handler() {
        this.stepCopy = Object.assign({}, this.value);
        this.$nextTick(() => { this.allowEdits = true; });
      },
    },
    duration: {
      deep: true,
      handler(nv, ov) {
        if (!isEqual(nv, ov)) {
          this.edit('duration');
        }
      },
    },
  },

  created() {
    this.stepCopy = Object.assign({}, this.value);
    this.$nextTick(() => { this.allowEdits = true; });
  },

  methods: {
    decodeHTML,
    /** Remake timepicker when clicking outside of it */
    rmkTimepicker() {
      this.timePickerKey++;
    },
    /** Send an edit XHR */
    edit(param) {
      if (!this.allowEdits) {
        return;
      }
      const params = {
        id: this.stepCopy.id,
        gw: this.stepCopy.gw,
        [param]: this.stepCopy[param],
      };
      this.SET_GW_STEP(params);
    },
    /** Send a delete XHR */
    onDeleteButton() {
      this.$emit('delete');
    },
    /** Triggered when checklists field is edited */
    checkListEdit() {
      const index = this.stepCopy.checklists.indexOf(0);
      if (index > -1) {
        this.$emit('newCheckList', this.value);
        this.stepCopy.checklists.splice(index, 1);
      }
      this.edit('checklists');
    },
    /** Checklist unticked from list */
    clCheckboxInput(checklist, idx) {
      calert(this.$t('message.GW_CL_UNTICK_OPTIONS'), [
        {
          title: this.$t('message.GW_CL_REMOVE'),
          action: () => {
            this.stepCopy.checklists.splice(idx, 1);
            this.edit('checklists');
          },
        },
        {
          title: this.$t('message.GW_CL_EDIT'),
          action: () => {
            this.$emit('editChecklist', checklist);
          },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Handle click on delete icon next to each checklist */
    deleteChecklist(checklist) {
      const { id } = checklist;
      const buttons = [
        {
          sClass: 'red',
          title: this.$t('message.GW_CL_DELETE'),
          action: () => {
            this.DELETE_GW_CHECKLIST({ id }).then((data) => {
              if (data instanceof Array) {
                toastr.success(this.$t('message.GW_CL_DELETED'));
              }
            });
          },
        },
        { title: this.$t('message.CANCEL') },
      ];
      if (checklist.items.some(item => item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR)) {
        buttons.splice(0, 0, {
          title: this.$t('message.GW_CL_DELETE_ORPHAN'),
          sClass: 'red',
          action: () => {
            this.DELETE_GW_CHECKLIST({ id, deleteOrphanIndicators: true }).then((data) => {
              if (data instanceof Array) {
                toastr.success(this.$t('message.GW_CL_DELETED'));
              }
            });
          },
        });
      }

      calert(this.$t('message.GW_CL_CONFIRM_DELETE'), buttons);
    },
    ...gwMapActions({
      SET_GW_STEP,
      DELETE_GW_CHECKLIST,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-step {
  position: relative;
  padding: .7rem;
  padding-left: 2rem;
  display: flex;

  &:not(:first-child) {
    margin-top: .7rem;
  }

  .dragging-indication {
    position: absolute;
    left: .5rem;
    top: 50%;
    transform: translateY(-50%);
  }

  &__basics, &__details {
    flex: 0 0 30%;
    width: 30%;
    position: relative;
  }

  &__checklists {
    flex: 0 0 40%;
    width: 40%;
    position: relative;
  }

  &__field {
    padding: .5rem;
    border-radius: $main-border-radius;
    cursor: pointer;

    &:hover {
      background: $main-hover-color;
    }
  }

  &__basics {

    &__delete {
      margin-top: 2.5rem;
    }
  }

  .add-button.unselect-team {
    display: inline;
    padding: 0px;
  }

  &__checklists {
    &__selected {
      .checkbox {
        flex: 1;
        min-width: 0px;
        max-width: 100%;
        margin-right: 0.4em;

        &__title {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
      }

      svg.icon {
        margin-left: 0.2em;
        padding: 0.3em;
        width: 1.4em;
        height: 1.4em;
        flex: 0 0 auto;
        cursor: pointer;

        &:hover {
          background-color: $color-hoverbtn-light-blue;
        }
        &.delete:hover {
          background-color: $color-hoverbtn-red;
        }
      }
    }

    &__add {
      margin-top: .7rem;
    }
  }

  &__placeholder {
    background: $color-droppable-active;
    height: 8em;
  }

}

@media (max-width: $smartphone-landscape) {
  .gw-step {
    padding-left: 32px;
  }
}
</style>
